package at.theatervogelweide.admin.contract.people

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.isImageUuid
import at.theatervogelweide.admin.contract.validate
import kotlinx.serialization.Serializable

// Addresses, e-mail addresses and phone numbers are personal data: only admins see them, and nothing logs them.

/** A row of the table. */
@Serializable
data class PersonListEntry(
    val id: Long,
    val version: Long,
    val firstName: String,
    val lastName: String,
    val email: String?,
    val imageUuid: String?,
    /** The productions the person had a role in. */
    val actCount: Int,
    /** The year of the latest of them. */
    val lastYear: Int?,
    /** The functions on the board, usually none or one. */
    val boardFunctions: List<String>,
    /** The roles, so the search finds people by them. */
    val roleNames: List<String>,
) {
    val name: String get() = fullName(firstName, lastName)

    /** Every word of [query] must appear in the name, the e-mail address, a role or a board function. */
    fun matches(query: String): Boolean {
        val words = query.lowercase().split(' ').filter { it.isNotBlank() }
        if (words.isEmpty()) {
            return true
        }

        val text = (listOf(name, email.orEmpty()) + roleNames + boardFunctions).joinToString("\n").lowercase()
        return words.all { it in text }
    }
}

/** A role in a production's cast, as the person's history lists it. */
@Serializable
data class PersonRole(
    val roleName: String,
    val actId: Long,
    val actTitle: String,
    val actYear: Int,
)

/** A saved person, as the editor loads it. */
@Serializable
data class PersonEditor(
    val id: Long,
    val version: Long,
    val draft: PersonDraft,
    /** Newest production first; roles are given in the production's cast, not here. */
    val roles: List<PersonRole>,
    val boardFunctions: List<String>,
    /** A sign-in account belongs to the person. */
    val hasAccount: Boolean,
    /** Mails were sent to the person. */
    val hasMails: Boolean,
) {
    val versionedId: VersionedId get() = VersionedId(id, version)

    /**
     * Why the person can't be deleted, or `null` if they can: like the public server, which only deletes a person
     * that no role, sign-in account or mail refers to.
     */
    val deleteBlocker: String?
        get() {
            val reasons = listOfNotNull(
                when (val acts = roles.distinctBy { it.actId }.size) {
                    0 -> null
                    1 -> "eine Rolle in einem Stück"
                    else -> "Rollen in $acts Stücken"
                },
                "ein Konto zum Anmelden".takeIf { hasAccount },
                "verschickte Mails".takeIf { hasMails },
            )
            if (reasons.isEmpty()) {
                return null
            }

            val list = if (reasons.size == 1) reasons.single() else reasons.dropLast(1).joinToString(", ") + " und " + reasons.last()
            return "Die Person hat $list und kann deshalb nicht gelöscht werden."
        }
}

/** The address of a person; all fields or none, as the database requires. */
@Serializable
data class AddressDraft(
    val street: String = "",
    val houseNumber: String = "",
    /** Digits only, without a leading zero: the database stores it as a number. */
    val zipcode: String = "",
    val city: String = "",
)

/** What the editor creates and saves. Blank e-mail addresses and phone numbers are stored as missing. */
@Serializable
data class PersonDraft(
    val firstName: String = "",
    val lastName: String = "",
    val email: String = "",
    val phone: String = "",
    val mobilePhone: String = "",
    val address: AddressDraft? = null,
    /** The photo, uploaded to `POST /api/images/persons` first. */
    val imageUuid: String? = null,
    /**
     * The version of the saved address the editor started from, `null` without one. The address has a row of its own,
     * which the public server can change or remove without changing the person, so saving checks it separately.
     */
    val addressVersion: Long? = null,
    /**
     * The photo's details (#105), keyed by its id like a record with several images; none without an entry, and an
     * entry of another image is ignored.
     */
    val imageDetails: Map<String, ImageDetails> = emptyMap(),
) {
    fun validate(): List<FieldError> = validate {
        field(FIRST_NAME, firstName) { required(); maxLength(255) }
        field(LAST_NAME, lastName) { required(); maxLength(255) }
        field(EMAIL, email) { maxLength(255); email() }
        field(PHONE, phone) { maxLength(255) }
        field(MOBILE_PHONE, mobilePhone) { maxLength(255) }
        address?.let { address ->
            field(STREET, address.street) { required(); maxLength(255) }
            field(HOUSE_NUMBER, address.houseNumber) { required(); maxLength(255) }
            field(ZIPCODE, address.zipcode) { required() }
            check(ZIPCODE, ZIPCODE_FORMAT.matches(address.zipcode.trim())) { "Bitte eine Postleitzahl aus 4 oder 5 Ziffern eingeben, ohne führende Null." }
            field(CITY, address.city) { required(); maxLength(255) }
        }
        check(IMAGE, imageUuid == null || isImageUuid(imageUuid)) { "Das Foto ist ungültig. Bitte neu laden." }
        // the details of a replaced photo are left out, like when saving
        imageDetails.filterKeys { it == imageUuid }.values.forEach { errors += it.validate(IMAGE) }
    }

    companion object {
        const val FIRST_NAME = "firstName"
        const val LAST_NAME = "lastName"
        const val EMAIL = "email"
        const val PHONE = "phone"
        const val MOBILE_PHONE = "mobilePhone"
        const val STREET = "street"
        const val HOUSE_NUMBER = "houseNumber"
        const val ZIPCODE = "zipcode"
        const val CITY = "city"
        const val IMAGE = "image"

        // a leading zero would be lost in the numeric column; Austrian postcodes have none
        private val ZIPCODE_FORMAT = Regex("^[1-9][0-9]{3,4}$")
    }
}

fun fullName(firstName: String, lastName: String): String = "$firstName $lastName".trim()

/** The two letters the avatar shows without a photo. */
fun initials(firstName: String, lastName: String): String =
    listOf(firstName, lastName).mapNotNull { it.trim().firstOrNull()?.uppercaseChar() }.joinToString("")
